import { ToastService } from 'src/app/base/services/toast.service';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { BeforeDownloadEventArgs, BeforeImageLoadEventArgs, BeforePopupOpenCloseEventArgs, BeforeSendEventArgs, ContextMenuSettingsModel, FileLoadEventArgs, FileManagerComponent, FileOpenEventArgs, FileSelectEventArgs, MenuClickEventArgs, MenuOpenEventArgs, SuccessEventArgs, ToolbarClickEventArgs, ToolbarCreateEventArgs, ToolbarSettingsModel, FileManagerModule, NavigationPaneService, DetailsViewService, ToolbarService } from '@syncfusion/ej2-angular-filemanager';
import { Observable, map } from 'rxjs';
import { AuthService } from 'src/app/base/services/auth.service';
import { environment } from 'src/environments/environment';
import { AssetStorageSingleEditComponent } from './asset-storage-edit/asset-storage-single-edit/asset-storage-single-edit.component';
import { IAssetStorageEntry } from './asset-storage-entry';
import { FilterDTO, IFileManagerDirectoryContentDTO, Role } from 'src/app/base/models/models.swagger.generated';
import { TranslateService } from '@ngx-translate/core';
import { AssetStorageFilterComponent } from './asset-storage-filter/asset-storage-filter.component';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { AssetStorageDirectoryPermissionComponent } from './asset-storage-directory-permission/asset-storage-directory-permission.component';
import { AssetStorageCollectionsComponent } from "./asset-storage-collections/asset-storage-collections.component";
import { AssetStorageMultiUploadComponent } from "./asset-storage-edit/asset-storage-multi-upload/asset-storage-multi-upload.component";

@Component({
    selector: 'app-asset-storage',
    templateUrl: './asset-storage.component.html',
    styleUrls: ['./asset-storage.component.scss'],
    imports: [TitleBarComponent, FileManagerModule, AssetStorageFilterComponent, AssetStorageDirectoryPermissionComponent, AssetStorageCollectionsComponent, AssetStorageSingleEditComponent, AssetStorageMultiUploadComponent],
    providers: [NavigationPaneService, ToolbarService, DetailsViewService]
})
export class AssetStorageComponent implements OnInit {

  public ajaxSettings: object = {
    url: environment.apiServerUrl + environment.assetStorageRoute + 'FileOperations',
    uploadUrl: environment.apiServerUrl + environment.assetStorageRoute + 'Upload',
  };

  public toolbarSettings: ToolbarSettingsModel = {
    items: ['Refresh'],
    visible: true
  };

  public contextMenuSettings: ContextMenuSettingsModel = {
    file: ['Download'],
    folder: null,
    layout: ['SortBy', 'Refresh', '|', 'SelectAll'],
    visible: true,
  };

  public filter: FilterDTO;
  private editContextMenuText = this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.edit-context-menu');
  private copyLinkContextMenuText = this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.copy-link-context-menu');
  private filterButtonText = this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.filter-button-text');
  private permissionsButtonText = this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.permissions-button-text');
  private collectionsButtonText = this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.collections-button-text');
  private toolbarItems = ['NewFolder', 'Upload', 'Rename', 'Refresh', 'Download', 'Delete', this.filterButtonText];

  private parentFolderHasPermissionRestrictions = false;

  @ViewChild(FileManagerComponent) public fileManager: FileManagerComponent;
  @ViewChild(AssetStorageSingleEditComponent) public singleEditComponent: AssetStorageSingleEditComponent;
  @ViewChild(AssetStorageMultiUploadComponent) public multiUploadComponent: AssetStorageMultiUploadComponent;
  @ViewChild('assetStorageFilterComponent') public assetStorageFilterComponent: AssetStorageFilterComponent;
  @ViewChild('directoryPermissionsComponent') public directoryPermissionsComponent: AssetStorageDirectoryPermissionComponent;
  @ViewChild('assetStorageCollectionsComponent') public assetStorageCollectionsComponent: AssetStorageCollectionsComponent;

  constructor(private authService: AuthService, private httpClient: HttpClient, private toastService: ToastService,
    private translateService: TranslateService) {
  }

  ngOnInit(): void {
    this.contextMenuSettings = {
      file: ['Rename', 'Download', 'Delete', this.editContextMenuText, this.copyLinkContextMenuText, this.collectionsButtonText],
      folder: ['Rename', 'Delete', this.permissionsButtonText, this.collectionsButtonText],
      layout: ['SortBy', 'Refresh', '|', 'NewFolder', '|', 'SelectAll'],
      visible: true,
    };
    this.toolbarSettings = {
      items: this.toolbarItems,
      visible: true
    };
  }

  public beforePopupOpen(args: BeforePopupOpenCloseEventArgs) {
    // disable file and folder drag from outside the file manager
    if (args.popupName === 'Upload') {
      this.toastService.showError('i18n.domain.assets.asset-storage.errors.drag-drop-upload-error.header', 'i18n.domain.assets.asset-storage.errors.drag-drop-upload-error.message');
      args.cancel = true;
    }
    if (args.popupName === 'Delete') {
      if (!this.deleteAllowed()) {
        args.cancel = true;
      }
    }
    if (args.popupName === 'Rename') {
      if (!this.deleteAllowed()) {
        args.cancel = true;
      }
    }
  }

  public beforeSend(args: BeforeSendEventArgs): void {
    let data: IFileManagerDirectoryContentDTO = JSON.parse((args.ajaxSettings as any).data);
    if (args.action === 'create' || args.action === 'rename') {
      // check if the folder name is valid
      if (data.name === undefined) {
        return;
      }
      // check if the file name contains invalid characters &%/?*|<>"':
      if (data.name.match(/[&%\/\?*|<>"':]/)) {
        this.toastService.showError('i18n.domain.assets.asset-storage.errors.folder-name-error.header', 'i18n.domain.assets.asset-storage.errors.folder-name-error.message');
        args.cancel = true;
        return;
      }
    }
    // disable file and folder drag from outside the file manager -> needs to be cancelled also here
    if (args.action === 'Upload') {
      args.cancel = true;
      return;
    }

    if (data && this.filter) {
      data.filter = this.filter;
    }

    // set the data back to the ajax settings as JSON string
    (args.ajaxSettings as any).data = JSON.stringify(data);
    (args.ajaxSettings as any).beforeSend = this.ajaxBeforeSend.bind(this);
    setTimeout(() => { this.authService.refreshTokenIfNecessary(null, false).subscribe() }, 0);
  }

  public beforeImageLoad(args: BeforeImageLoadEventArgs): void {
    args.imageUrl = (args.fileDetails?.at(0) as any)?.fileLink ?? 'assets/img/document_unspecified.svg';
  }

  public beforeDownload(args: BeforeDownloadEventArgs): void {
    args.cancel = true;
    const assetsToDownload = (args.data as any).data as IAssetStorageEntry[];
    // at the moment we only support file download within the browser
    assetsToDownload?.forEach((entry: IAssetStorageEntry) => {
      if (entry.isFile) {
        const link = this.createInvisibleDownloadLink(entry.fileLink, entry.name).subscribe((link: HTMLAnchorElement) => {
          link.click();
          link.remove();
        });
      }
      else {
        this.toastService.showError('i18n.domain.assets.asset-storage.errors.is-folder-error.header', 'i18n.domain.assets.asset-storage.errors.is-folder-error.message', entry.name);
      }
    });
  }

  public onEditFormClose() {
    this.fileManager.refresh();
  }

  public onToolbarClick(args: ToolbarClickEventArgs) {
    if (args.item.id?.endsWith('_filter')) {
      this.assetStorageFilterComponent.open();
    }
    if (!args.item.id?.endsWith('_upload')) {
      return;
    }
    args.cancel = true;
    const uploadPath = ((args.fileDetails.at(0) as any)?.path ?? '') + '/';
    this.multiUploadComponent.assignAssets(uploadPath);
  }

  public onFileOpen(args: FileOpenEventArgs) {

    // get the extension of the file
    const asset = args.fileDetails as IAssetStorageEntry;
    if (asset?.isFile) {
      if (!this.canEditAsset(asset)) {
        return;
      }
      //if file is of type then open in the preview
      const extension = asset.name.toLowerCase().split('.').pop();
      // check if extension is jpg, jpeg, png
      if (['jpg', 'jpeg', 'png'].includes(extension)) {
        // show image in new tab since preview shows image distorted
        args.cancel = true;
        window.open(asset.fileLink, '_blank');
        return; // this will open the preview window
      }
      // open the file edit dialog
      const uploadPath = (asset.path ?? '') + '/';
      args.cancel = true;

      this.singleEditComponent.editAsset(asset, uploadPath);
    }
  }

  public onFileSelect(args: FileSelectEventArgs) {
    // when no item is selected anymore, then toggle toolbar to permissions of parent folder
    if (args.action === 'unselect' && this.fileManager.selectedItems.length === 0) {
      if (this.parentFolderHasPermissionRestrictions) {
        this.fileManager.disableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Download', 'Delete']);
      }
      return;
    }

    // if the user has no write permission, then disable toolbar items
    var isFile = (args.fileDetails as any).isFile;
    if (this.authService.doesUserHaveAnyRole([Role.AssetStorageGroupMember])) {
      if ((args.fileDetails as any)?.permission?.write === false) {
        this.fileManager.disableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Download', 'Delete']);
        if (!isFile) {
          this.parentFolderHasPermissionRestrictions = true;
          return;
        }
      } else {
        this.fileManager.enableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Download', 'Delete']);
      }
    }
    //Prevent clicking on folders without opening them to erase parentFolderHasPermissionRestrictions
    if (this.fileManager.selectedItems.length == 0) {
      this.parentFolderHasPermissionRestrictions = false;
    }

    if (isFile && (args.fileDetails as any)?.isStaticFileName) {
      this.fileManager.disableToolbarItems(['Rename']);
    } else if (!this.authService.doesUserHaveAnyRole([Role.AssetStorageGroupMember]) || (args.fileDetails as any)?.permission?.write === true) {
      this.fileManager.enableToolbarItems(['Rename']);
    }
  }

  public onFileLoad(args: FileLoadEventArgs) {
    const asset = args.fileDetails as IAssetStorageEntry;

    //disable toolbar items for root folder on startup
    if (asset.path == "/") {
      if (this.authService.doesUserHaveAnyRole([Role.AssetStorageGroupMember])) {
        if ((args.fileDetails as any)?.permission?.write === false) {
          this.fileManager.disableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Download', 'Delete']);
        }
      }
      if (this.authService.doesUserHaveAnyRole([Role.AssetStorageDownload])) {
        this.fileManager.disableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Delete']);
      }
    }
    if (this.authService.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin])) {
      this.fileManager.enableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Delete']);
    }
    else {
      if (this.parentFolderHasPermissionRestrictions) {
        this.fileManager.disableToolbarItems(['NewFolder', 'Upload', 'Rename', 'Delete']);
      }
    }

    if (this.authService.doesUserHaveAnyRole([Role.AssetStorageUpload])) {
      this.fileManager.enableToolbarItems(['Upload']);
    }

    if (asset?.isStaticFileName) {
      const element = args.element.querySelector('.e-list-text') as HTMLElement;
      const lockIcon = document.createElement('span');
      lockIcon.className = 'e-icons icon-locked';
      lockIcon.style.marginRight = '5px';
      lockIcon.style.verticalAlign = 'middle';
      //lockIcon.style.color = '#b60b25';
      element.insertBefore(lockIcon, element.firstChild);
    }
    if (asset?.linkedAssetCollections?.length > 0) {
      const element = args.element.querySelector('.e-list-text') as HTMLElement;
      const lockIcon = document.createElement('span');
      lockIcon.className = 'e-icons icon-navaid--dme';
      lockIcon.style.marginRight = '5px';
      lockIcon.style.verticalAlign = 'middle';
      //lockIcon.style.color = '#b60b25';
      lockIcon.title = this.translateService.instant('i18n.domain.assets.asset-collections.link-asset-collection.icon-tooltip') + ': ' + asset.linkedAssetCollections.map(c => c.name).join(', ');
      element.insertBefore(lockIcon, element.firstChild);

    }

    // if the user is admin or asset storage admin and if the folder has a permission defined, then show an icon in the folder
    if (asset?.isFile) {
      return;
    }
    if (this.authService.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin])) {
      if (asset?.directoryPermissions?.length > 0) {
        // make font size bold for the folder name
        const element = args.element.querySelector('.e-list-text') as HTMLElement;
        // add a lock icon
        const lockIcon = document.createElement('span');
        lockIcon.className = 'e-icons icon-group--security';
        lockIcon.style.marginRight = '5px';
        lockIcon.style.verticalAlign = 'middle';
        //lockIcon.style.color = 'red';
        lockIcon.title = this.translateService.instant('i18n.domain.assets.asset-storage.permission-icon-tooltip');
        element.insertBefore(lockIcon, element.firstChild);
      }
    }

  }

  public menuOpen(args: MenuOpenEventArgs | any) {
    var isRootFolder = args.fileDetails[0].path === '/';
    if (isRootFolder) {
      args.cancel = true;
      return;
    }

    if (!this.authService.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin])) {
      // remove permissions and collections menu item
      args.items = args.items.filter((item: any) => {
        return ![this.permissionsButtonText].includes(item.text) && ![this.collectionsButtonText].includes(item.text);
      });
    }
    if (!this.authService.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin])) {
      if (args.fileDetails[0].isFile) {
        args.items = args.items.filter((item: any) => {
          return item.id === 'overview_cm_download' || item.text === this.copyLinkContextMenuText;
        });
      }
      else {
        args.cancel = true;
      }
    }

    if (this.authService.doesUserHaveAnyRole([Role.AssetStorageGroupMember])) {
      if (args.fileDetails[0].permission?.write === false) {
        // remove rename, upload, delete, create folder menu items using the commands map

        if (args.fileDetails[0].isFile) {
          args.items = args.items.filter((item: any) => {
            return item.id === 'overview_cm_download' || item.text === this.copyLinkContextMenuText;
          });
        }
        else {
          args.cancel = true;
        }
      }
    }

    if (args.fileDetails[0].isFile && args.fileDetails[0].isStaticFileName) {
      args.items = args.items.filter((item: any) => {
        return item.text !== 'Rename';
      });
    }

    for (var i = 0; i < args.items.length; i++) {
      if (args.items[i].text === this.editContextMenuText) {
        args.items[i].iconCss = 'e-icons e-fe-rename';
      }
      if (args.items[i].text === this.permissionsButtonText) {
        args.items[i].iconCss = 'e-icons icon-group--security';
      }
      if (args.items[i].text === this.copyLinkContextMenuText) {
        args.items[i].iconCss = 'e-icons icon-copy--link';
      }
      if (args.items[i].text === this.collectionsButtonText) {
        args.items[i].iconCss = 'e-icons icon-navaid--dme';
      }
    }
  }

  // event for custom menu item
  public menuClick(args: MenuClickEventArgs | any) {
    const asset = args.fileDetails[0] as IAssetStorageEntry;
    if (args.item.text === this.editContextMenuText) {
      if (asset?.isFile) {
        const uploadPath = (asset.path ?? '') + '/';
        args.cancel = true;
        this.singleEditComponent.editAsset(asset, uploadPath);
      }
    }
    if (args.item.text === this.permissionsButtonText) {
      if (!asset?.isFile) {
        args.cancel = true;
        this.directoryPermissionsComponent.openDirectoryPermissions(asset);
      }
    }
    if (args.item.text === this.collectionsButtonText) {
      if(this.fileManager.selectedItems.length > 1) {
        // get all selected assets
        const assets = args.fileDetails as IAssetStorageEntry[];
        this.assetStorageCollectionsComponent.openCollections(assets);
      } else {
      this.assetStorageCollectionsComponent.openCollections([asset]);
      }
    }
    if (args.item.text === this.copyLinkContextMenuText) {
      // copy link to clipboard
      args.cancel = true;
      navigator.clipboard.writeText(asset.fileLink).then(() => {
        this.toastService.showSuccess('i18n.domain.assets.asset-storage.edit-asset-storage.copy-link-success.header', 'i18n.domain.assets.asset-storage.edit-asset-storage.copy-link-success.message');
      }, () => {
        this.toastService.showError('i18n.domain.assets.asset-storage.edit-asset-storage.copy-link-error.header', 'i18n.domain.assets.asset-storage.edit-asset-storage.copy-link-error.message');
      });
    }
  }

  public onFilterUpdated(filter: FilterDTO) {
    this.filter = filter;
    this.fileManager.refresh();
  }

  public onToolbarCreate(args: ToolbarCreateEventArgs) {
    // find the filter button and replace with a custom one
    let filterButton = args.items.find((item) => item.id?.endsWith('_' + this.filterButtonText.toLowerCase()));
    let index = args.items.indexOf(filterButton);
    // replace the filter button with a custom one
    if (filterButton) {
      args.items[index] = {
        id: filterButton.id,
        prefixIcon: 'e-icons e-icon-filter ' + (this.filter?.predicates?.length > 0 ? 'e-filtered' : ''),
        tooltipText: this.translateService.instant('i18n.domain.assets.asset-storage.edit-asset-storage.filter-button-tooltip'),
        text: this.filterButtonText
      } as any;
    };
  }

  public onDirectoryPermissionsChanged() {
    this.fileManager.refresh();
  }

  public onAjaxCallSuccess(args: SuccessEventArgs) {
    // This hack is necessary, because the file manager does not refresh the view with the new values after a rename operation
    // The actual issue can be reproduced by selecting a folder (which has children) and then rename it.
    // The folder is renamed, but when you try to open a child folder you get an error
    if (args.action === 'rename') {
      const result = args.result as { cwd: { name: string, path: string } };
      this.fileManager.currentItemText = result.cwd.name;
      this.fileManager.path = result.cwd.path;
      // this part is required to fix the error, when you search for folders and then rename a folder
      var obj = this.fileManager.breadcrumbbarModule.searchObj;
      obj.value = '';
      this.fileManager.refresh();
      this.fileManager.breadcrumbbarModule.searchObj = obj;
    }
  }

  private ajaxBeforeSend(args: any) {
    args.httpRequest.setRequestHeader('Authorization', 'Bearer ' + this.authService.getBearerToken());
  }

  private createInvisibleDownloadLink(url: string, filename: string): Observable<HTMLAnchorElement> {
    // using directly the url in link.href does not work, because the browser then rather opens a new page with the url than downloading the file
    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      map((result: Blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(result);
        link.download = filename;
        return link;
      }));
  }

  private canEditAsset(asset: IAssetStorageEntry): boolean {
    return this.authService.doesUserHaveAnyRole([Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin, Role.Admin, Role.AssetStorageUpload])
      || (this.authService.doesUserHaveAnyRole([Role.AssetStorageGroupMember]) && (asset as any)?.permission?.write);
  }

  public get isDragAndDropAllowed(): boolean {
    return this.authService.doesUserHaveAnyRole([Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin, Role.Admin]);
  }

  private deleteAllowed(): boolean {
    return this.authService.doesUserHaveAnyRole([Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin, Role.Admin, Role.AssetStorageGroupMember]);
  }

  public onCollectionsChange() {
    this.fileManager.refresh();
  }
}
